import { type Dispatch, type SetStateAction, useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { deleteVipTier, type VipTier } from './apiHelpers';

export interface DeleteVipTierConfirmationModalProps {
  closeModal: () => void;
  isOpen: boolean;
  onDeleted: () => void;
  restaurantId: string;
  setVipTierToDelete: Dispatch<SetStateAction<VipTier | undefined>>;
  vipTier: VipTier;
}

export const DeleteVipTierConfirmationModal = ({
  closeModal,
  isOpen,
  onDeleted,
  restaurantId,
  setVipTierToDelete,
  vipTier,
}: DeleteVipTierConfirmationModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteVipTier = async (): Promise<void> => {
    setIsDeleting(true);
    try {
      await deleteVipTier(restaurantId, vipTier.id);
      setVipTierToDelete(undefined);
      closeModal();
      onDeleted();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({ message: 'An error occurred' });
      }
      reportAppError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = (): void => {
    setVipTierToDelete(undefined);
    closeModal();
  };

  return (
    <Modal
      ariaLabel="confirm delete VIP tier modal"
      isOpen={isOpen}
      onClose={handleClose}
      subtitle={`Are you sure you would like to delete the VIP tier "${vipTier.name}"?`}
    >
      <ModalActions>
        <Button
          isDisabled={isDeleting}
          label="Cancel"
          onClick={handleClose}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          isDisabled={isDeleting}
          isLoading={isDeleting}
          label="Yes, delete VIP tier"
          onClick={() => {
            void handleDeleteVipTier();
          }}
          variant={ButtonVariants.Primary}
        />
      </ModalActions>
    </Modal>
  );
};
